import { useCallback, useEffect, useRef, useState } from "react";
import { useBufferedEroundSum } from "./useBufferedEroundSum";

type Period = { year: number; month: number };

/**
 * Owns the eRound-sum field state for the validation dialogs and pre-fills it
 * from the buffered per-month eRound sum. Prefill wins on month/year change,
 * but a manual entry for the current period is never overwritten by a late
 * buffer resolution.
 */
export function useEroundPrefill(selectedPeriod: Period) {
  const { year, month } = selectedPeriod;
  const { lookup, ready } = useBufferedEroundSum();
  const [eroundSum, setEroundSumState] = useState<number | null>(null);
  const [eroundPrefilled, setEroundPrefilled] = useState(false);

  // lastPeriodRef is initialised to the starting period so the first ready-run
  // for that period is not treated as a change; manualEditRef records that the
  // operator has edited the field for the current period.
  const manualEditRef = useRef(false);
  const lastPeriodRef = useRef(`${year}-${month}`);

  useEffect(() => {
    if (!ready) return;
    const key = `${year}-${month}`;
    if (lastPeriodRef.current !== key) {
      lastPeriodRef.current = key;
      manualEditRef.current = false; // new period → prefill may apply again
    }
    if (manualEditRef.current) return; // operator edited this period → keep it
    const buffered = lookup(year, month);
    setEroundSumState(buffered);
    setEroundPrefilled(buffered != null);
  }, [ready, lookup, year, month]);

  const setEroundSum = useCallback((value: number | null) => {
    manualEditRef.current = true;
    setEroundSumState(value);
    setEroundPrefilled(false);
  }, []);

  return { eroundSum, setEroundSum, eroundPrefilled };
}
